/** How well a percentage was reached; NEUTRAL while the judgment layer is closed. */
export enum AchievementTiers {
    NEUTRAL = "NEUTRAL",
    WARNING = "WARNING",
    SUCCESS = "SUCCESS",
    STRONG_SUCCESS = "STRONG_SUCCESS",
}

/**
 * The colors of an achievement bar and of the result box beside it.
 *
 * Every value is a complete Tailwind class name — the v4 CLI scans the source for whole classes, so
 * these must never be assembled from fragments.
 */
export interface AchievementColors {
    /** Bar and box share one outline; only their fills differ. */
    border: string;
    barFill: string;
    boxFill: string;
}

/** Below this the result is flagged rather than praised. */
const WARNING_BELOW = 50;
/** Above this the fill deepens to the darker green. */
const STRONG_ABOVE = 80;

/**
 * `isColored` is the gated judgment layer: a color says "you did badly here" as plainly as a
 * number does, so it needs a scored test *and* the score reveal. NEUTRAL until then — the bar still
 * shows how much was reached, it just puts no verdict on it (see `docs/evaluation`).
 */
export const getAchievementTier = (percentage: number, isColored: boolean): AchievementTiers => {
    if (!isColored) {
        return AchievementTiers.NEUTRAL;
    }
    if (percentage < WARNING_BELOW) {
        return AchievementTiers.WARNING;
    }
    if (percentage > STRONG_ABOVE) {
        return AchievementTiers.STRONG_SUCCESS;
    }
    return AchievementTiers.SUCCESS;
};

/**
 * The one place a tier becomes colours, so the summary box and the per-exercise cards never disagree
 * about what a percentage looks like.
 */
const colorsByTier: Record<AchievementTiers, AchievementColors> = {
    [AchievementTiers.NEUTRAL]: {
        border: "border-p6-grey-mid",
        barFill: "bg-p6-grey-light",
        boxFill: "bg-background-light",
    },
    [AchievementTiers.WARNING]: {
        border: "border-p6-buttercup-900",
        barFill: "bg-p6-buttercup-800",
        boxFill: "bg-p6-orange-light-pastel",
    },
    [AchievementTiers.SUCCESS]: {
        border: "border-p6-green",
        barFill: "bg-p6-green-mid",
        boxFill: "bg-forest-green-pastel",
    },
    [AchievementTiers.STRONG_SUCCESS]: {
        border: "border-p6-green",
        barFill: "bg-p6-green",
        boxFill: "bg-forest-green-pastel",
    },
};

export const getAchievementColors = (tier: AchievementTiers): AchievementColors => colorsByTier[tier];
